/**
 * Publieke pagina: uitnodiging accepteren — teamlid wordt gekoppeld aan bestaande organisatie.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';
import { TurnstileField } from '../components/TurnstileField';
import { api, fetchPublicConfig } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

function PasswordField({
  id,
  value,
  onChange,
  reveal,
  onRevealChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  reveal: boolean;
  onRevealChange: (v: boolean) => void;
}) {
  return (
    <div className="relative">
      <input
        id={id}
        type={reveal ? 'text' : 'password'}
        required
        minLength={8}
        value={value}
        onChange={(ev) => onChange(ev.target.value)}
        className="w-full rounded-lg border border-border py-2 pl-3 pr-11 text-text"
        autoComplete="new-password"
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label="Wachtwoord tonen (ingedrukt houden)"
        className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-text-muted hover:bg-surface hover:text-text"
        onPointerDown={(e) => {
          e.preventDefault();
          onRevealChange(true);
        }}
        onPointerUp={() => onRevealChange(false)}
        onPointerLeave={() => onRevealChange(false)}
        onPointerCancel={() => onRevealChange(false)}
      >
        <Eye className="h-4 w-4 shrink-0" aria-hidden />
      </button>
    </div>
  );
}

export function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const token = searchParams.get('token')?.trim() ?? '';

  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [orgName, setOrgName] = useState('');
  const [email, setEmail] = useState('');

  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(false);

  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPublicConfig()
      .then((c) => {
        if (!cancelled) setTurnstileSiteKey(c.turnstile_site_key ?? '');
      })
      .catch(() => {
        if (!cancelled) setTurnstileSiteKey('');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setPreviewLoading(false);
      setPreviewError('Deze pagina hoort bij een uitnodigingslink uit je e-mail. Vraag je beheerder om een nieuwe link als nodig.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const p = await api.getInvitationPreview(token);
        if (cancelled) return;
        if (!p.valid) {
          setPreviewError(p.message ?? 'Deze uitnodiging is niet meer geldig.');
          return;
        }
        setOrgName(p.organization_name ?? '');
        setEmail(p.email ?? '');
      } catch {
        if (!cancelled) setPreviewError('Kon de uitnodiging niet controleren. Probeer het later opnieuw.');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== password2) {
      setError('Wachtwoorden komen niet overeen.');
      return;
    }
    if (!token) return;
    if (turnstileSiteKey === null) {
      setError('Een ogenblik, beveiliging laden…');
      return;
    }
    if (turnstileSiteKey !== '' && !turnstileToken) {
      setError('Rond de beveiligingscontrole hieronder af.');
      return;
    }
    setLoading(true);
    try {
      await api.authAcceptInvite({
        token,
        password,
        password_confirm: password2,
        ...(turnstileSiteKey ? { turnstile_token: turnstileToken ?? undefined } : {}),
      });
      await refreshUser();
      setOk(true);
      setTimeout(() => navigate('/dashboard', { replace: true }), 1800);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Account aanmaken mislukt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-page-enter">
      <SeoMeta
        path="/uitnodiging"
        noindex
        title="Uitnodiging accepteren"
        description="Accepteer je uitnodiging voor CalqTool™ en maak je wachtwoord aan om bij je team te horen."
      />
      <AnimatedSection delay={0.05}>
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Uitnodiging' }]} />
        <div className="mx-auto max-w-md rounded-lg border border-border bg-surface-card p-8">
          <h1 className="mb-1 text-2xl font-bold text-text">Meedoen met je team</h1>
          <p className="mb-6 text-sm text-text-muted">
            Je beheerder heeft je uitgenodigd om in hetzelfde CalqTool-bedrijfsaccount te werken. Kies hieronder je
            wachtwoord om je account te activeren.
          </p>

          {previewLoading && <p className="text-sm text-text-muted">Uitnodiging controleren…</p>}

          {!previewLoading && previewError && (
            <div
              className="rounded-lg border border-amber-200/90 bg-amber-50 px-3 py-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-50"
              role="alert"
            >
              {previewError}
              <p className="mt-3">
                <Link to="/login" className="font-medium text-primary hover:underline">
                  Naar inloggen
                </Link>
                {' · '}
                <Link to="/" className="font-medium text-primary hover:underline">
                  Home
                </Link>
              </p>
            </div>
          )}

          {!previewLoading && !previewError && orgName && (
            <>
              <div className="mb-6 rounded-lg border border-primary/25 bg-primary/5 px-4 py-3 text-sm text-text">
                <p className="font-medium text-text">Organisatie</p>
                <p className="text-text">{orgName}</p>
                {email && (
                  <>
                    <p className="mt-2 font-medium text-text">Je inlognaam (e-mail)</p>
                    <p className="text-text-muted">{email}</p>
                  </>
                )}
              </div>

              {ok ? (
                <p className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm text-text" role="status">
                  <strong>Je bent binnen.</strong> Je wordt doorgestuurd naar het dashboard…
                </p>
              ) : (
                <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
                  <div>
                    <label htmlFor="inv-pw" className="mb-1 block text-sm font-medium text-text">
                      Wachtwoord kiezen
                    </label>
                    <PasswordField id="inv-pw" value={password} onChange={setPassword} reveal={reveal} onRevealChange={setReveal} />
                    <p className="mt-1 text-xs text-text-muted">Minimaal 8 tekens.</p>
                  </div>
                  <div>
                    <label htmlFor="inv-pw2" className="mb-1 block text-sm font-medium text-text">
                      Wachtwoord bevestigen
                    </label>
                    <PasswordField
                      id="inv-pw2"
                      value={password2}
                      onChange={setPassword2}
                      reveal={reveal}
                      onRevealChange={setReveal}
                    />
                  </div>

                  {turnstileSiteKey !== null && turnstileSiteKey !== '' && (
                    <div className="rounded-lg border border-border bg-surface px-3 py-2">
                      <p className="mb-2 text-xs text-text-muted">Beveiligingscontrole</p>
                      <TurnstileField siteKey={turnstileSiteKey} onToken={setTurnstileToken} />
                    </div>
                  )}

                  {error && (
                    <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-100" role="alert">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-lg bg-primary py-3 font-medium text-white transition-all hover:bg-primary-alt disabled:opacity-60"
                  >
                    {loading ? 'Bezig…' : 'Account activeren'}
                  </button>
                </form>
              )}
            </>
          )}

          <p className="mt-6 text-center text-sm text-text-muted">
            <strong className="text-text">Nog geen uitnodiging?</strong> Wil je zelf starten met een proefperiode voor je
            bedrijf?{' '}
            <Link to="/login?register=1" className="text-primary hover:underline">
              Account aanmaken
            </Link>{' '}
            (dat wordt een <em>nieuw</em> bedrijfsaccount — niet hetzelfde als meedoen via uitnodiging).
          </p>
        </div>
      </AnimatedSection>
    </div>
  );
}
